// The RECEIPT dialogs, lifted out of components/ClientPortal/index.tsx (FS-4, owner 2026-10-19).
//
// They were the last two document dialogs the portal still kept to itself, which is why the staff
// workspace's Sales › Receipts tab has been read-only: panes/Documents.tsx said so in as many words
// ("its limited (remark/bank) edit form was not extracted"). Moved here BEHIND THE SAME
// MerchantDocApi the other dialogs go through, so there is one implementation of issuing a receipt
// and one of correcting it, whichever side of the login it is opened from.
//
// ⛔ Behaviour must stay identical for the portal: the bodies below are the portal's own, moved
// rather than rewritten. What changed, and only this:
//   · the two `api.portal*` calls became `docApi.*` members (the portal maps them straight back);
//   · the hand-rolled overlay became the shared DocWindow — same width, same alignment, now with an
//     X, the Esc key and a dirty check on every exit;
//   · EditReceiptModal sends the REASON the server has always demanded. See the comment on `save`:
//     without it the portal could not save a Thai receipt at all.
//
// ⛔ What a receipt edit is NOT allowed to be: a form over its figures. A receipt is the tax invoice
// for a service business (s.78/1 + s.86/4), so its face — buyer, date, values, VAT — is not
// rewritable while the buyer holds a copy; only the internal remark and which bank account prints
// may be corrected, and anything else is void-and-reissue. That is the SERVER's rule, enforced in
// portalDocs.ts docPatchRoute (`if (kind === 'receipt')` writes remark and bank_account_id, and
// nothing else, then answers `{ ok: true, limited: true }`) — this screen renders those two fields
// because that is what the server will write, not as an opinion of its own.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { money } from '../../utils/region';
import { docPlainOf } from '../ClientPortal/plainMode';
import RichTextEditor from '../RichTextEditor';
import { PickerSelect } from './CreateDocModal';
import { DocWindow, useDocWindowGate } from './shell';
import { dayFirstDeep } from './shared';
import { InBooksBanner } from './InBooksBanner';
import { MerchantReceiptApi, TFn, inp, lbl, btn, btnGhost, r2, useRemarkImages, docTypeWord } from './shared';

// A receipt IS the tax invoice for a service business (s.78/1 + s.86/4), so its face — buyer, date,
// values, VAT — is not rewritable while the buyer holds a copy. Only the internal remark and which
// bank account is printed may be corrected; anything else is void-and-reissue.
export function EditReceiptModal({ doc, profile, docApi, onClose, onDone, fullscreen, layer, audience = 'client' }: {
  doc: any; profile: any; docApi: MerchantReceiptApi; onClose: () => void; onDone: () => void;
  /** FS-3/FS-4: staff hosts only — the portal keeps its 560 card. */
  fullscreen?: boolean; layer?: number;
  /** Batch 11 R1: whose words the "already in the books" banner speaks (CreateDocModal's prop, same default). */
  audience?: 'staff' | 'client';
}) {
  const { t } = useTranslation();
  const banks: any[] = profile.banks || [];
  const [remark, setRemark] = useState(doc.remark || '');
  const { onImage: onRemarkImage, seed: seedRemark, finalise: finaliseRemark } = useRemarkImages(docApi);
  // See CreateDocModal: the seed rewrites the Remark on the system's behalf, so the dirty baseline
  // rebases on it rather than reading as typing nobody did.
  const [remarkSeeded, setRemarkSeeded] = useState(0);
  useEffect(() => {
    let alive = true;
    seedRemark(String(doc.remark || '')).then(h => {
      if (!alive) return;
      setRemark(c => (c === (doc.remark || '') && h !== c ? h : c));
      setRemarkSeeded(n => n + 1);
    });
    return () => { alive = false; };
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  const [bankAccountId, setBankAccountId] = useState(doc.bank_account_id || '');
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  // The two fields the server will actually write, and nothing else to lose.
  const snapshot = JSON.stringify({ remark, bankAccountId });
  const { dirty, close } = useDocWindowGate(snapshot, onClose, busy, remarkSeeded);
  const save = async () => {
    setBusy(true); setErr('');
    const body: any = { remark: finaliseRemark(remark), bankAccountId };
    // ⛔ THE SERVER'S RULE, NOT THIS SCREEN'S. portalDocs.ts docPatchRoute refuses any edit of a
    // document that books an output tax line unless the body carries a reason — 400 "Say why this
    // document is being corrected", and for a receipt that is `booksTax('receipt', r) =
    // !docPlain(r)`: every Thai receipt, since the receipt IS the tax invoice for a service
    // business (s.78/1). Whether THIS receipt is one is the server's answer, read off
    // GET /docs/receipt/:id/editable (`needsReason`) and handed in by the host as __needsReason —
    // never re-derived here, so a plain receipt is not asked a question its edit does not need.
    // ⚠️ Until this, the form sent neither a reason nor a way to type one, so saving a Thai
    // receipt's remark failed with that message and no field to answer it (measured 2026-10-19).
    // Same prompt and same words as CreateDocModal's correction, because it is the same event.
    if (doc?.__needsReason) {
      const why = window.prompt(t('p_cdoc_correct_prompt', { docNo: doc.doc_no }));
      if (why === null) { setBusy(false); return; }
      if (!why.trim()) { setErr(t('p_cdoc_err_reason')); setBusy(false); return; }
      body.reason = why.trim();
    }
    try { await docApi.updateReceipt(doc.id, body); onDone(); }
    catch (e: any) { setErr(e?.message || t('p_err_save_failed')); setBusy(false); }
  };
  // (Batch 12 tail T2) The Cancel / Save row: pinned in the full-screen window's footer (staff), inline as before (portal).
  const foot = (
    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: fullscreen ? 0 : 14 }}>
      <button style={btnGhost} onClick={close}>{t('p_btn_cancel')}</button>
      <button style={{ ...btn, opacity: busy ? .5 : 1 }} disabled={busy} onClick={save}>{busy ? t('p_erec_saving') : t('p_cdoc_save_changes')}</button>
    </div>
  );
  return (
    <DocWindow title={t('p_erec_title', { docNo: doc.doc_no })} width={560}
      onClose={onClose} busy={busy} dirty={dirty} fullscreen={fullscreen} layer={layer} footer={fullscreen ? foot : undefined}>
      <>
        {/* Batch 11 R1 (COA-D9): the entries this receipt has in the books — read-only. */}
        <InBooksBanner rows={doc.inBooks} who={audience} style={{ marginBottom: 10 }} />
        <div style={{ fontSize: 12.5, color: '#B45309', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '9px 11px', marginBottom: 12, lineHeight: 1.55 }}>
          {/* Outside Thailand a receipt is not a tax invoice (merchantMode.ts) — it only records money received. */}
          {docPlainOf(doc) ? t('pi_erec_note') : t('p_erec_warning')}
        </div>
        <div style={{ fontSize: 12.5, color: '#5b6b79', marginBottom: 10 }}>
          {doc.client_name} · {doc.payment_date || doc.issue_date} · {t('p_erec_received', { amount: money(doc.amount, 2, profile?.id) })}{Number(doc.wht_amount) ? ` ${t('p_erec_wht', { amount: money(doc.wht_amount, 2, profile?.id) })}` : ''}
        </div>
        <label style={lbl}>{t('p_erec_bank')}</label>
        <select style={inp} value={bankAccountId} onChange={e => setBankAccountId(e.target.value)}>
          <option value="">{t('p_erec_bank_none')}</option>
          {banks.map(b => <option key={b.id} value={b.id}>{b.account_name} · {b.account_number}</option>)}
        </select>
        <label style={lbl}>{t('p_erec_remark')}</label>
        <RichTextEditor value={remark} onChange={setRemark} onImage={onRemarkImage} extras minHeight={72}
                        placeholder={t('p_cdoc_remark_ph')} />
        {doc?.id ? docApi.evidence?.('receipt', String(doc.id)) : null}
        {err && <div style={{ color: '#D0021B', fontSize: 12.5, marginTop: 8 }}>{err}</div>}
        {!fullscreen && foot}
      </>
    </DocWindow>
  );
}

// The server's coded refusals (portalDocs.ts merchantCreateReceipt), in the operator's language: it
// sends a `code` and the figures beside its English, and this window words them. Anything else it
// says is shown as it said it.
//   · invoice_fully_receipted / invoice_nothing_outstanding — the picker's own rule says nothing is
//     left to receipt (typically: the other door receipted it while this dialog was open);
//   · nothing_outstanding_credited (round 4, §B-7) — nothing is left because a credit note took the rest:
//     the sentence says so (receipted X, credited Y) rather than "already fully receipted";
//   · period_closed — the payment date falls in an accounting period the books have closed;
//   · receipt_exceeds_outstanding (round 3) — cash + WHT is more than is still outstanding (typically:
//     the other door receipted part of it while this dialog was open);
//   · wht_exceeds_outstanding (round 4, item 4) — more WHT than the buyer still has to withhold;
//   · amount_negative (round 3) — a negative cash or WHT figure; amount_zero (round 4) — nothing at all.
//   · document_void (verification 2026-10-27) — the document was voided while this dialog was open.
// ⛔ (Round 4, item 8) Each refusal names the document by ITS OWN type — "Debit note DN-…", "Tax invoice
// TIV-…" — from the `docType` the server sends (docTypeWord: the workspace's own type words); a bill
// reads exactly as before.
/** (Round 4.1, decision 5) The next step for WHT typed on a document issued without withholding — the server's own
 *  judgement (whtNoneNextStep): edit it first where its full edit still opens; (round 4.1 review) void its receipts
 *  first where only they stand in the way and each may be voided (`voidNos`), then edit, then record the cash and the
 *  WHT together; else record the cash only.
 *  (Round 4.1 re-verification) `audience` 'staff' — the workspace door — cannot void a merchant document (the
 *  workspace has no void, the portal admits only the client): its step asks the client to void them in the portal. */
const whtNoneText = (t: TFn, editable: any, voidNos: any, vars: Record<string, any>, audience: 'staff' | 'client' = 'client'): string =>
  String(editable ? t('p_crec_wht_none_edit', vars)
    : String(voidNos || '').trim() ? t(audience === 'staff' ? 'p_crec_wht_none_void_staff' : 'p_crec_wht_none_void', { ...vars, receiptNos: String(voidNos) })
      : t('p_crec_wht_none_cash', vars));
const refusalText = (e: any, t: TFn, merchantId?: string | null, audience: 'staff' | 'client' = 'client'): string => {
  const p = e?.payload && typeof e.payload === 'object' ? e.payload : {};
  const dw = docTypeWord(t, p.docType);
  const m = (v: any) => money(Number(v) || 0, 2, merchantId);
  if (p.code === 'invoice_fully_receipted')
    return t('p_crec_err_settled_doc', { ...dw, invoiceNo: p.invoiceNo, receiptNos: (Array.isArray(p.receiptNos) ? p.receiptNos : []).join(', ') });
  if (p.code === 'invoice_nothing_outstanding') return t('p_crec_err_nothing_owed_doc', { ...dw, invoiceNo: p.invoiceNo });
  // (COA-D6) A tax invoice issued for an invoice already paid — its money was receipted on that invoice.
  if (p.code === 'tax_invoice_for_paid')
    return t('p_crec_err_tax_for', { invoiceNo: p.invoiceNo, forNo: p.forNo, receiptNos: (Array.isArray(p.receiptNos) ? p.receiptNos : []).join(', ') });
  if (p.code === 'document_void') return t('p_crec_err_void', { ...dw, invoiceNo: p.invoiceNo });
  if (p.code === 'nothing_outstanding_credited')
    return t('p_crec_err_credited', { ...dw, invoiceNo: p.invoiceNo, receipted: m(p.receipted), credited: m(p.credited) });
  if (p.code === 'period_closed') return t('p_crec_err_period_closed', { date: p.date, from: p.periodStart, to: p.periodEnd });
  // (Review 2026-11-02) The month it would book into is filed, or the foreign bill has no rate it could follow (portalDocs.ts receiptDateGate): said before Issue.
  if (p.code === 'receipt_filed') return t('p_crec_err_filed', { ym: p.params?.ym, date: p.params?.date });
  if (p.code === 'receipt_fx_rate') return t('p_crec_err_fx_rate', { docNo: p.params?.docNo, currency: p.params?.currency });
  if (p.code === 'receipt_exceeds_outstanding')
    return t('p_crec_err_exceeds', { ...dw, invoiceNo: p.invoiceNo, received: m(p.received), outstanding: m(p.outstanding) });
  // (Round 4.1, decision 5) …on a document issued WITHOUT withholding, followed by the next step that exists.
  if (p.code === 'wht_exceeds_outstanding')
    return t('p_crec_err_wht_exceeds', { ...dw, invoiceNo: p.invoiceNo, wht: m(p.wht), left: m(p.whtOutstanding) })
      + (p.whtNone ? ` ${whtNoneText(t, p.whtEditable, p.whtVoidNos, { ...dw, invoiceNo: p.invoiceNo }, audience)}` : '');
  if (p.code === 'amount_negative') return t('p_crec_err_negative');
  if (p.code === 'amount_zero') return t('p_crec_err_zero');
  return '';
};

export function CreateReceiptModal({ merchantId, plain, docApi, onClose, onDone, fullscreen, layer, audience = 'client', presetInvoice }: {
  merchantId?: string | null; plain?: boolean; docApi: MerchantReceiptApi; onClose: () => void; onDone: () => void;
  /** FS-3/FS-4: staff hosts only — the portal keeps its 760 centred card. */
  fullscreen?: boolean; layer?: number;
  /** Batch 11 R3: whose words the supersede refusal speaks — the portal's never say "reverse". The superseded line is a
   *  hand-entered one no document holds, so staff are sent to Journal entries by the sentence (W2-46), no button. */
  audience?: 'staff' | 'client';
  /** A quotation's Create Receipt (owner 2026-11-02): the window opens on this bill. ⛔ The staff host's alone. Read by its id, because the picker reads one page of 50. */
  presetInvoice?: { id: string; docNo: string };
}) {
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState<any[]>([]);
  // No "today" default (owner 2026-09-03): the receipt is dated and NUMBERED by the day the money
  // arrived, and the silent default put August collections into the September series.
  const [invoiceId, setInvoiceId] = useState(''); const [paymentDate, setPaymentDate] = useState('');
  const [cash, setCash] = useState(''); const [wht, setWht] = useState('');
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  // Bumped when the preset fills the window, so the dirty check rebases on it: nobody typed that.
  const [presetDone, setPresetDone] = useState(0);
  useEffect(() => {
    if (!presetInvoice) { docApi.unpaidInvoices().then((d: any) => setInvoices(d.rows || [])); return; }
    let alive = true;
    Promise.all([docApi.unpaidInvoices(), docApi.unpaidInvoices('', presetInvoice.id)]).then(([page, one]: any[]) => {
      if (!alive) return;
      const row = (one?.rows || []).find((r: any) => r.id === presetInvoice.id);
      setInvoices(row ? [row, ...(page?.rows || []).filter((r: any) => r.id !== row.id)] : (page?.rows || []));
      if (row) { setInvoiceId(row.id); const { c, w } = owedOf(row); setCash(c > 0 ? String(c) : (w > 0 ? '0' : '')); setWht(w > 0 ? String(w) : ''); }
      else setErr(t('aw_conv_receipt_gone', { docNo: presetInvoice.docNo }));
      setPresetDone(n => n + 1);
    }).catch((e: any) => { if (alive) setErr(e?.message || t('p_crec_err')); });
    return () => { alive = false; };
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  const inv = invoices.find(i => i.id === invoiceId);
  // Batch 11 R3 (design §3.5.4 row 3): a receipt that would supersede a hand-entered VAT line still in the books is
  // refused (409 superseded_line_posted) — asked here when the invoice is chosen, so Save is disabled with the sentence.
  const [preLock, setPreLock] = useState<any>(null);
  // (Review 2026-11-02) …and what it would meet on the date in the form and in the bill's currency (the same answer's `gate`, merchantCreateReceipt's own gates): a closed period, a filed month, a foreign
  // bill with no rate. A refused POST pages the admins, so it is asked as the date changes, said here with Issue not offered, and asked once more at Issue.
  const [preGate, setPreGate] = useState<any>(null);
  useEffect(() => { setPreLock(null); setPreGate(null); }, [invoiceId]);
  useEffect(() => {
    if (!invoiceId || !docApi.receiptPreflight) return;
    let alive = true;
    docApi.receiptPreflight(invoiceId, paymentDate).then((d: any) => { if (alive) { setPreLock(d?.lock ? { ...d.lock, line: d.line || null } : null); setPreGate(d?.gate || null); } }).catch(() => { /* the server still refuses */ });
    return () => { alive = false; };
  }, [invoiceId, paymentDate]);   // eslint-disable-line react-hooks/exhaustive-deps
  // Day-first on a staff screen; the portal's sentences stay as they are (shared.ts dayFirstDeep).
  const gateWords = (raw: any): string => { if (!raw) return ''; const rf = audience === 'staff' ? dayFirstDeep(raw) : raw; return refusalText({ payload: rf }, t, merchantId, audience) || String(rf.error || ''); };
  const preGateText = gateWords(preGate);
  // A receipt belongs to its invoice's world (server merchantCreateReceipt): the picked invoice decides,
  // and before one is picked the merchant's own mode does.
  const plainDoc = inv ? docPlainOf(inv) : !!plain;
  // What is still owed, as the SERVER measures it (routes/portalDocs.ts OUTSTANDING_SQL — the figure it
  // refuses on and remarks against; owner 2026-10-20, receipts round 3): the picker's rows carry the
  // outstanding WITH the buyer's withholding and the part of it that is WHT still to come. The rest is
  // cash. Never re-derived here: before round 3 this dialog worked it out net (net_to_pay − paid, WHT
  // pro rata), so a second instalment of a 21,400 / 600-WHT invoice offered 10,100 + 291.35 for the
  // 10,400 + 300 actually owed, and the server then printed "short by 308.65" on the tax invoice.
  const owedOf = (i: any) => {
    const o = r2(Number(i?.outstanding) || 0);
    // A plain document records no withholding: the buyer pays the invoice in full.
    const w = docPlainOf(i) ? 0 : Math.min(o, r2(Number(i?.outstanding_wht) || 0));
    return { o, w, c: r2(o - w) };
  };
  const owed = inv ? owedOf(inv) : { o: 0, w: 0, c: 0 };
  const outCash = owed.c;
  const outWht = owed.w;

  // Picking an invoice fills both figures in — the common case is "they paid it in full". Both stay
  // editable for a part payment or a buyer who withheld a different amount than expected.
  const pick = (id: string) => {
    setInvoiceId(id);
    const i = invoices.find(x => x.id === id);
    if (!i) { setCash(''); setWht(''); return; }
    const { c, w } = owedOf(i);
    setCash(c > 0 ? String(c) : (w > 0 ? '0' : '')); setWht(w > 0 ? String(w) : '');
  };
  const settled = r2((Number(cash) || 0) + (Number(wht) || 0));
  // A figure below zero is refused by the server (round 3, amount_negative) — so it is never announced
  // as "a part payment"; the window says it will be refused, before Issue is pressed.
  const negative = (Number(cash) || 0) < 0 || (Number(wht) || 0) < 0;
  // Against what is owed: short → recorded as a part payment (as before); over → the server refuses it
  // (round 3), so the window says so before Issue is pressed.
  const short = !!inv && !negative && settled < owed.o - 0.005;
  const over = !!inv && !negative && settled > owed.o + 0.005;
  // (Round 4, item 4) More WHT than the buyer still has to withhold — the server refuses it
  // (wht_exceeds_outstanding), so the window says so before Issue and does not send it. Nothing at all
  // (0 cash + 0 WHT) is refused too (amount_zero) and is not sent either.
  const whtOver = !!inv && !negative && (Number(wht) || 0) > owed.w + 0.005;
  const zero = !!inv && !negative && settled <= 0.005;
  // (Round 4.1, decision 5) WHT typed for a document ISSUED WITHOUT withholding: the band also says what to do
  // next — edit the document first where the server says it may still be edited (`wht_editable`, the edit
  // route's own rule), else record the cash only and pass the certificate to the accountant.
  const whtNone = whtOver && Number(inv?.wht_amount) <= 0.005;
  // ⛔ (Round 4.1, decision 4) Issue is offered on NO band the server refuses: more than is outstanding, a
  // negative figure, more WHT than is still to come, nothing at all. Each band says why, before Issue.
  const refusedBand = over || negative || whtOver || zero;
  // (Round 4.1, decision 2) What credit notes took off the picked document — then Outstanding is not
  // Total − Paid, and the strip says so: "Received X · Credited Y".
  const credited = r2(Number(inv?.credited) || 0);
  // s.86/4: when this receipt will itself be the tax invoice (VAT-registered merchant, the invoice
  // was not a tax invoice) it names both parties in Thai. The server says which Thai names are
  // missing (receipt_th_gaps, the same rule the receipt prints with); the English one still prints,
  // and the form says so exactly as the invoice form does (owner 2026-10-03, Q9).
  const thGaps: string[] = Array.isArray(inv?.receipt_th_gaps) ? inv.receipt_th_gaps : [];
  // What the operator has filled in. A receipt is ISSUED, never saved as a draft, so losing this to
  // a stray backdrop click means re-reading the bank statement to find the figures again.
  const snapshot = JSON.stringify({ invoiceId, paymentDate, cash, wht });
  const { dirty, close } = useDocWindowGate(snapshot, onClose, busy, presetDone);

  const submit = async () => {
    setBusy(true); setErr('');
    // Asked once more: the answer on screen may be for a date typed a moment ago. Unanswered, the issue itself answers.
    if (docApi.receiptPreflight) {
      let rf: any = null;
      let lk: any = null;
      try { const d: any = await docApi.receiptPreflight(invoiceId, paymentDate); rf = d?.gate || null; lk = d?.lock ? { ...d.lock, line: d.line || null } : null; } catch { rf = null; }
      // (Review 2026-11-03) …and the VAT line it would supersede, which may have posted itself since the invoice was chosen (server services/autoPost.ts): Save goes, and the sentence comes.
      if (lk) { setPreLock(lk); setBusy(false); return; }
      if (rf) { setPreGate(rf); setBusy(false); return; }
    }
    try { await docApi.createReceipt({ invoiceId, paymentDate, cashAmount: Number(cash) || 0, whtAmount: Number(wht) || 0 }); onDone(); }
    catch (e: any) {
      setErr(refusalText(e, t, merchantId, audience) || e?.message || t('p_crec_err')); setBusy(false);
      // The books moved under this window (round 3: typically the other door receipted part of it while
      // it was open). Its figures came from the picker when it opened, so the picked row is read again —
      // the strip and the hint against what is owed are then the server's figures, not the stale ones
      // (which called the exact remainder the server named "a part payment"). The typed figures stay.
      const p = e?.payload && typeof e.payload === 'object' ? e.payload : {};
      if ((p.code === 'receipt_exceeds_outstanding' || p.code === 'wht_exceeds_outstanding') && inv?.doc_no) {
        const id = invoiceId;
        docApi.unpaidInvoices(String(inv.doc_no)).then((d: any) => {
          const fresh = (d?.rows || []).find((r: any) => r.id === id);
          if (fresh) setInvoices(list => list.map(r => (r.id === id ? fresh : r)));
        }).catch(() => {});
      }
    }
  };
  // (Batch 12 tail T2) The Cancel / Issue row: pinned in the full-screen window's footer (staff), inline as before (portal).
  const foot = (
    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: fullscreen ? 0 : 14 }}>
      <button style={btnGhost} onClick={close}>{t('p_btn_cancel')}</button>
      {/* Not sent when the window already knows the server refuses it (round 4.1, decision 4: every refused band;
          batch 11 R3: the receipt preflight's ledger lock). */}
      <button style={{ ...btn, opacity: busy || !invoiceId || !paymentDate || refusedBand || !!preLock || !!preGateText ? .5 : 1 }} disabled={busy || !invoiceId || !paymentDate || refusedBand || !!preLock || !!preGateText} onClick={submit}>{busy ? t('p_crec_saving') : t('p_crec_issue')}</button>
    </div>
  );
  return (
    <DocWindow title={t('p_crec_title')} titleSize={17} subtitle={plainDoc ? t('pi_crec_subtitle') : t('p_crec_subtitle')}
      width={760} centred onClose={onClose} busy={busy} dirty={dirty} fullscreen={fullscreen} layer={layer} footer={fullscreen ? foot : undefined}>
      <>
        <label style={lbl}>{t('p_crec_invoice')}</label>
        <PickerSelect
          value={inv ? { id: inv.id, label: `${inv.doc_no} · ${inv.client_name}` } : null}
          options={invoices.map(i => {
            const ow = owedOf(i);
            return { id: i.id, label: `${i.doc_no} · ${i.client_name}`,
              // ⛔ (Round 4, §B-5) "Outstanding" is the WHOLE figure, with the withholding — as every list,
              // card and Telegram print it. Where WHT is still to come it is shown as PART of it: "X
              // outstanding — of which WHT to come Y, cash Z". Never the cash part under the word.
              sub: ow.w > 0
                ? t('p_crec_option_outstanding_wht', { amount: money(ow.o, 2, merchantId), wht: money(ow.w, 2, merchantId), cash: money(ow.c, 2, merchantId) })
                : t('p_crec_option_outstanding', { amount: money(ow.o, 2, merchantId) }) };
          })}
          onSearch={term => { docApi.unpaidInvoices(term).then((d: any) => setInvoices(d.rows || [])).catch(() => {}); }}
          onPick={o => pick(o.id)}
          placeholder={t('p_crec_select_invoice')} />
        {inv && (
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 8, padding: '9px 12px',
                        background: '#f2f5f7', borderRadius: 8, fontSize: 13 }}>
            <span>{t('p_crec_invoice_total')} <b>{money(inv.total_amount, 2, merchantId)}</b></span>
            {credited > 0.005
              ? <span data-crec-credited="">{t('p_owed_received_credited', { received: money(inv.paid_amount, 2, merchantId), credited: money(credited, 2, merchantId) })}</span>
              : Number(inv.paid_amount) > 0 && <span>{t('p_crec_already_paid')} <b>{money(inv.paid_amount, 2, merchantId)}</b></span>}
            {/* (Round 4, §B-5) Outstanding is the whole figure; the WHT still to come is said as part of it. */}
            <span>{t('p_crec_outstanding')} <b>{money(owed.o, 2, merchantId)}</b>
              {outWht > 0 && <span style={{ color: '#B45309' }}> {t('p_crec_of_which', { wht: money(outWht, 2, merchantId), cash: money(outCash, 2, merchantId) })}</span>}</span>
          </div>
        )}
        {thGaps.length > 0 && (
          <div style={{ marginTop: 8, fontSize: 12, color: '#B45309', background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 8, padding: '7px 11px', fontWeight: 600 }}>
            ⚠ {t('p_cdoc_need_th_name', {
              who: thGaps.map(g => t(g === 'seller' ? 'p_cdoc_th_seller' : 'p_cdoc_th_buyer')).join(' + '),
            })}
          </div>
        )}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 4 }}>
          <div style={{ flex: '1 1 190px', minWidth: 170 }}>
            <label style={lbl}>{t('p_crec_payment_date')}</label>
            <input style={inp} type="date" value={paymentDate} onChange={e => setPaymentDate(e.target.value)} />
            <div style={{ fontSize: 11.5, color: paymentDate ? '#5b6b79' : '#B45309', marginTop: 3 }}>
              {paymentDate ? t('p_crec_date_ok', { yyyymm: paymentDate.slice(0, 7) }) : t('p_crec_date_required')}
            </div>
          </div>
          {/* min=0 (round 3): a receipt records money that arrived; the server refuses a negative figure too. */}
          <div style={{ flex: '1 1 190px', minWidth: 170 }}><label style={lbl}>{plainDoc ? t('pi_crec_cash') : t('p_crec_cash')}</label><input style={inp} type="number" min={0} value={cash} onChange={e => setCash(e.target.value)} /></div>
          {!plainDoc && <div style={{ flex: '1 1 190px', minWidth: 170 }}><label style={lbl}>{t('p_crec_wht')}</label><input style={inp} type="number" min={0} value={wht} onChange={e => setWht(e.target.value)} /></div>}
        </div>
        <div style={{ fontSize: 13, marginTop: 10, padding: '9px 12px', borderRadius: 8,
                      background: refusedBand ? '#FDECEC' : short ? '#FEF3C7' : '#EAF6EF' }}>
          {plainDoc ? t('pi_crec_settling', { total: money(settled, 2, merchantId) })
            : t('p_crec_settling', { total: money(settled, 2, merchantId), cash: money(Number(cash) || 0, 2, merchantId), wht: money(Number(wht) || 0, 2, merchantId) })}
          {short && <> <span style={{ color: '#B45309' }}>{t('p_crec_differs', { amount: money(owed.o, 2, merchantId) })}</span></>}
          {over && <> <span style={{ color: '#B3261E', fontWeight: 600 }}>{t('p_crec_exceeds', { amount: money(owed.o, 2, merchantId) })}</span></>}
          {negative && <> <span style={{ color: '#B3261E', fontWeight: 600 }}>{t('p_crec_negative')}</span></>}
          {whtOver && <> <span style={{ color: '#B3261E', fontWeight: 600 }}>{t('p_crec_wht_over', { wht: money(owed.w, 2, merchantId) })}</span></>}
          {whtNone && <div data-crec-wht-none="" style={{ color: '#B3261E', marginTop: 4 }}>{whtNoneText(t, inv?.wht_editable, inv?.wht_void_nos, { ...docTypeWord(t, inv?.doc_type), invoiceNo: inv?.doc_no }, audience)}</div>}
          {zero && <> <span style={{ color: '#B3261E', fontWeight: 600 }}>{t('p_crec_zero')}</span></>}
        </div>
        {!plainDoc && <div style={{ fontSize: 12, color: '#5b6b79', marginTop: 6 }}>{t('p_crec_attach_hint')}</div>}
        {preLock && (
          <div data-ll-superseded={audience} style={{ marginTop: 8, fontSize: 12.5, color: '#1E3A8A', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, padding: '8px 11px', lineHeight: 1.55 }}>
            {(() => {
              // (W2 ship gate) Staff are never told to reverse an entry posted in a period closed since — it is not
              // reversed (the server's closed branch); the client's words never say "reverse" at all.
              const shut = (preLock.entries || []).find((e: any) => e?.closed && !e?.reversalDraft);
              const p = { lineNo: String(preLock.line?.doc_no || ''), docNos: (preLock.entries || []).map((e: any) => e.docNo).join(', ') };
              if (audience !== 'staff') return t('p_ll_receipt_superseded', p);
              const dmy = (iso: string) => { const d = String(iso || '').split('-'); return d.length === 3 ? `${d[2]}/${d[1]}/${d[0]}` : String(iso || ''); };
              return shut ? t('aw_ll_receipt_superseded_closed', { ...p, from: dmy(shut.closed.start), to: dmy(shut.closed.end) }) : t('aw_ll_receipt_superseded', p);
            })()}
          </div>
        )}
        {!!preGateText && <div data-crec-gate="" style={{ color: '#B42318', fontSize: 12.5, marginTop: 8, lineHeight: 1.5 }}>{preGateText}</div>}
        {err && <div style={{ color: '#D0021B', fontSize: 12.5, marginTop: 8 }}>{err}</div>}
        {!fullscreen && foot}
      </>
    </DocWindow>
  );
}
